import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button, Form, Spinner } from 'react-bootstrap';
import {
  downloadDataBackup,
  getDataSummary,
  restoreDataBackup,
  wipeOvrRecords,
} from '../services/api';
import { setAlert } from '../store/slices/alertSlice';
import ConfirmActionModal from './ConfirmActionModal';

const readApiError = async (error, fallback) => {
  const data = error?.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      return parsed.error || fallback;
    } catch (parseError) {
      return fallback;
    }
  }
  return data?.error || fallback;
};

const DataMaintenance = () => {
  const dispatch = useDispatch();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyAction, setBusyAction] = useState('');
  const [restoreFile, setRestoreFile] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);

  const loadSummary = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getDataSummary();
      setSummary(data);
    } catch (error) {
      dispatch(setAlert({
        type: 'danger',
        message: error.response?.data?.error || 'Could not load data summary',
      }));
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const busy = Boolean(busyAction);

  const runPendingAction = async () => {
    if (!pendingAction) return;
    const action = pendingAction;
    setBusyAction(action.type);
    try {
      if (action.type === 'backup') {
        const response = await downloadDataBackup();
        const blob = new Blob([response.data], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        const match = String(response.headers?.['content-disposition'] || '').match(/filename="([^"]+)"/);
        link.href = url;
        link.download = match?.[1] || `irs-backup-${Date.now()}.json`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        dispatch(setAlert({ type: 'success', message: 'Backup downloaded. Store this file in a safe place.' }));
      } else if (action.type === 'restore') {
        const body = new FormData();
        body.append('file', restoreFile);
        const { data } = await restoreDataBackup(body);
        dispatch(setAlert({ type: 'success', message: data.message || 'Backup restored.' }));
        setRestoreFile(null);
        await loadSummary();
      } else if (action.type === 'reset') {
        const { data } = await wipeOvrRecords();
        dispatch(setAlert({ type: 'success', message: data.message || 'System reset to default.' }));
        await loadSummary();
      }
      setPendingAction(null);
    } catch (error) {
      dispatch(setAlert({
        type: 'danger',
        message: action.type === 'backup'
          ? await readApiError(error, 'Could not create the backup')
          : (error.response?.data?.error || 'Action failed'),
      }));
    } finally {
      setBusyAction('');
    }
  };

  if (loading && !summary) {
    return (
      <div className="admin-page d-flex justify-content-center py-5">
        <Spinner animation="border" role="status" />
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div>
          <h3>Backup & Reset</h3>
          <p>
            Download a backup, restore one, or reset the system. Each button asks for confirmation
            before it runs. Reset keeps your Admin login, roles, departments, and email settings.
          </p>
        </div>
      </div>

      {summary && (
        <div className="data-summary card p-3 shadow-sm">
          <span><strong>{summary.visibleIncidents ?? summary.incidents}</strong> visible OVR</span>
          <span><strong>{summary.hiddenIncidents || 0}</strong> archived</span>
          <span><strong>{summary.users}</strong> users</span>
          <span><strong>{summary.departments}</strong> departments</span>
          <span><strong>{summary.roles}</strong> roles</span>
          <span><strong>{summary.refNo?.nextRefNo || '—'}</strong> next OVR no.</span>
        </div>
      )}

      <div className="maintenance-grid">
        <section className="card p-3 shadow-sm data-tool-card">
          <h4>Download backup</h4>
          <p>Save users, departments, OVR records, settings, and attachments. Keep the file private.</p>
          <Button
            type="button"
            disabled={busy}
            onClick={() => setPendingAction({
              type: 'backup',
              title: 'Download backup?',
              message: 'Create and download a full backup now?',
              confirmLabel: 'Download backup',
              variant: 'primary',
            })}
          >
            {busyAction === 'backup' ? 'Preparing...' : 'Download backup'}
          </Button>
        </section>

        <section className="card p-3 shadow-sm data-tool-card">
          <h4>Restore backup</h4>
          <p>Replace current data with a backup file. Sign in again if your account was replaced.</p>
          <Form.Group controlId="restore-file" className="mb-3">
            <Form.Label>Backup file</Form.Label>
            <Form.Control
              type="file"
              accept="application/json,.json"
              onChange={(event) => setRestoreFile(event.target.files?.[0] || null)}
            />
          </Form.Group>
          <Button
            type="button"
            variant="warning"
            disabled={busy || !restoreFile}
            onClick={() => setPendingAction({
              type: 'restore',
              title: 'Restore this backup?',
              message: `Replace current data with "${restoreFile?.name}"? This overwrites existing records.`,
              confirmLabel: 'Restore backup',
              variant: 'warning',
            })}
          >
            {busyAction === 'restore' ? 'Restoring...' : 'Restore backup'}
          </Button>
        </section>

        <section className="card p-3 shadow-sm data-tool-card data-tool-danger">
          <h4>Reset system</h4>
          <p>
            Removes OVR records, other users, notifications, and audit logs. Brand and email templates
            return to defaults. Your Admin account stays, and the next OVR number does not go backwards.
          </p>
          <Button
            type="button"
            variant="danger"
            disabled={busy}
            onClick={() => setPendingAction({
              type: 'reset',
              title: 'Reset the system?',
              message: 'This factory reset cannot be undone unless you have a backup. Continue?',
              confirmLabel: 'Reset system',
              variant: 'danger',
            })}
          >
            {busyAction === 'reset' ? 'Resetting...' : 'Reset system to default'}
          </Button>
        </section>
      </div>

      <ConfirmActionModal
        show={Boolean(pendingAction)}
        title={pendingAction?.title}
        message={pendingAction?.message}
        confirmLabel={pendingAction?.confirmLabel}
        variant={pendingAction?.variant || 'primary'}
        busy={busy}
        onCancel={() => !busy && setPendingAction(null)}
        onConfirm={runPendingAction}
      />
    </div>
  );
};

export default DataMaintenance;
